import { useEffect, useState, useCallback } from "react";
import { Sparkles, Loader2, Plus, Pencil, Trash2, Save, X, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "Cuidado de mascotas", tags: [], faq: [], meta_description: "", keywords: [], published: true };

export const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/blog?all=true");
      setPosts(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Escribe un tema para el artículo"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { topic });
      setEditing({
        ...EMPTY,
        title: data.title || topic,
        slug: data.slug || "",
        excerpt: data.excerpt || "",
        content: data.content || "",
        category: data.category || "Cuidado de mascotas",
        tags: data.tags || [],
        keywords: data.keywords || [],
        meta_description: data.meta_description || "",
        faq: (data.faq || []).map((f) => ({ question: f.question || f.q || "", answer: f.answer || f.a || "" })),
        cover_image: "",
      });
      setOpen(true);
      toast.success("¡Artículo generado! Revísalo y publícalo.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo generar");
    } finally { setGenerating(false); }
  };

  const openEdit = (p) => { setEditing({ ...p }); setOpen(true); };
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!editing.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/blog/${editing.id}`, payload);
      else await api.post("/blog", payload);
      toast.success("Artículo guardado");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"?`)) return;
    await api.delete(`/blog/${p.id}`); toast.success("Eliminado"); load();
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-[#E3E7E2] p-5">
        <h3 className="font-display text-lg font-bold text-charcoal mb-1">Generar artículo con IA</h3>
        <p className="text-sm text-muted-moss mb-3">Escribe un tema y la IA redactará un artículo optimizado para SEO y AEO. Luego lo revisas y publicas.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="blog-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Ej: ¿Cada cuánto debo bañar a mi perro?" className="inp flex-1" />
          <button data-testid="blog-generate-button" onClick={generate} disabled={generating} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-coral text-white font-semibold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60 whitespace-nowrap">
            {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} Generar
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-moss">{posts.length} artículo(s)</p>
        <button onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:bg-sage/40"><Plus className="w-4 h-4" /> Manual</button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="w-6 h-6 animate-spin text-coral" /></div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`admin-blog-${p.id}`} className="flex gap-3 bg-white rounded-2xl border border-[#E3E7E2] p-3">
              <img src={p.cover_image} alt="" className="w-20 h-16 rounded-xl object-cover bg-cream-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase text-coral">{p.category}</span>
                  {p.published ? <Eye className="w-3.5 h-3.5 text-teal" /> : <EyeOff className="w-3.5 h-3.5 text-muted-moss" />}
                </div>
                <p className="font-semibold text-sm text-charcoal line-clamp-1">{p.title}</p>
                <p className="text-xs text-muted-moss line-clamp-1">{p.excerpt}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <button onClick={() => openEdit(p)} className="grid place-items-center w-8 h-8 rounded-lg bg-cream hover:bg-sage/50"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p)} className="grid place-items-center w-8 h-8 rounded-lg bg-cream text-muted-moss hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-3xl bg-cream border-[#E3E7E2] max-h-[92vh] overflow-y-auto">
          <DialogTitle className="font-display text-xl font-bold text-charcoal">{editing?.id ? "Editar artículo" : "Nuevo artículo"}</DialogTitle>
          <DialogDescription className="sr-only">Formulario de artículo</DialogDescription>
          {editing && (
            <div className="space-y-3">
              <Field label="Título"><input value={editing.title} onChange={(e) => set("title", e.target.value)} className="inp" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Categoría"><input value={editing.category} onChange={(e) => set("category", e.target.value)} className="inp" /></Field>
                <Field label="Imagen de portada (URL)"><input value={editing.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} className="inp" placeholder="https://... (opcional)" /></Field>
              </div>
              <Field label="Resumen"><textarea rows={2} value={editing.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="inp resize-none" /></Field>
              <Field label="Meta descripción (SEO)"><input value={editing.meta_description} onChange={(e) => set("meta_description", e.target.value)} className="inp" /></Field>
              <Field label="Contenido (Markdown)"><textarea rows={10} value={editing.content} onChange={(e) => set("content", e.target.value)} className="inp resize-y font-mono text-xs leading-relaxed" /></Field>
              <Field label="Keywords (separadas por coma)"><input value={(editing.keywords || []).join(", ")} onChange={(e) => set("keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="inp" /></Field>
              <Field label="Tags (separados por coma)"><input value={(editing.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="inp" /></Field>
              {editing.faq && editing.faq.length > 0 && (
                <div className="rounded-xl bg-sage/30 p-3 text-xs text-charcoal">
                  <p className="font-semibold mb-1">{editing.faq.length} preguntas frecuentes incluidas (AEO)</p>
                  <ul className="list-disc pl-4 space-y-0.5">{editing.faq.map((f, i) => <li key={i}>{f.question}</li>)}</ul>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" /> Publicado</label>
              <button data-testid="blog-save-button" onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Guardar
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-muted-moss">{label}</span><div className="mt-1">{children}</div></label>
);
